import * as React from 'react';

export type RegisterContentPlayerNavLeading = (
    node: React.ReactNode | null,
    revision?: string
) => void;

export const ContentPlayerNavLeadingContext = React.createContext<RegisterContentPlayerNavLeading | null>(
    null,
);

export function useContentPlayerNavLeadingRegistrar(): RegisterContentPlayerNavLeading | null {
    return React.useContext(ContentPlayerNavLeadingContext);
}

export type UseSinglePageNavPlacementOptions = {
    /**
     * Maps to `LayoutConfig.inlineSinglePageNav`. When true, always render `navNode`
     * inline; do not register with ContentPlayer’s leading slot.
     */
    inlineSinglePageNav?: boolean;
    /** Fingerprint for footer leading slot remounts when answer state changes. */
    revision?: string;
};

/**
 * Registers `navNode` with ContentPlayer’s nav-leading slot when the host supplies a
 * registrar and `inlineSinglePageNav` is not true; otherwise returns `navNode` as
 * `inlineNav` for embedding in the player body.
 */
export function useSinglePageNavPlacement(
    navNode: React.ReactNode,
    options?: UseSinglePageNavPlacementOptions
): { inlineNav: React.ReactNode | null } {
    const registerNavLeading = useContentPlayerNavLeadingRegistrar();
    const forceInline = options?.inlineSinglePageNav === true;
    const useExternalSlot = !forceInline && registerNavLeading != null;

    const revision = options?.revision;

    React.useLayoutEffect(() => {
        if (!useExternalSlot || registerNavLeading == null) {
            return;
        }
        registerNavLeading(navNode, revision);
        return () => {
            registerNavLeading(null);
        };
    }, [useExternalSlot, registerNavLeading, navNode, revision]);

    const inlineNav = useExternalSlot ? null : navNode;
    return { inlineNav };
}
